import { create } from 'zustand';
import { ApiStatus } from '../types/enums/ApiStatus';
import {
  BadRequestException,
  NetworkException,
  ServerErrorException,
} from '../utils/apiException';
import { parseErrorCode, parseErrorsWithErrorCode } from '../utils/apiUtils';

interface ApiStore {
  status: ApiStatus;
  data?: Object;
  errorCode?: string;
  errors?: Object;
}

interface ApiActions {
  apiCall: (data?: Object, isRefresh?: boolean) => Promise<void>;
  apiInit: (data?: Object) => void;
  load: (data?: Object) => Promise<Object>;
  reset: () => void;
}

export const createApiStore = (loadFn: (data?: Object) => Promise<Object>) =>
  create<ApiStore & ApiActions>((set, get) => ({
    status: ApiStatus.Initial,
    apiInit: (data?: Object) => {
      set({ status: ApiStatus.Success, data });
    },
    apiCall: async (data?: Object, isRefresh: boolean = false) => {
      set({ status: isRefresh ? ApiStatus.Refreshing : ApiStatus.Connecting });
      try {
        // await new Promise((resolve) => setTimeout(resolve, 1000));
        const result = await get().load(data);
        set({ status: ApiStatus.Success, data: result });
      } catch (error) {
        if (error instanceof BadRequestException) {
          const errorData = parseErrorsWithErrorCode(error.message);
          set({
            status: isRefresh
              ? ApiStatus.ValidationErrorOnRefresh
              : ApiStatus.ValidationError,
            errorCode: errorData.errorCode,
            errors: errorData.errors,
          });
        } else if (error instanceof ServerErrorException) {
          const errorCode = parseErrorCode(error.message);
          set({
            status: errorCode
              ? isRefresh
                ? ApiStatus.ValidationErrorOnRefresh
                : ApiStatus.ValidationError
              : isRefresh
              ? ApiStatus.ServerErrorOnRefresh
              : ApiStatus.ServerError,
            errorCode: errorCode || undefined,
          });
        } else if (error instanceof NetworkException) {
          set({
            status: isRefresh
              ? ApiStatus.ConnectionErrorOnRefresh
              : ApiStatus.ConnectionError,
          });
        } else {
          set({
            status: isRefresh
              ? ApiStatus.ServerErrorOnRefresh
              : ApiStatus.ServerError,
          });
        }
      }
    },
    reset: () => {
      set({
        status: ApiStatus.Initial,
        data: undefined,
        errorCode: undefined,
        errors: undefined,
      });
    },
    load: loadFn,
  }));
